import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Trophy, ArrowLeft, AlertCircle } from 'lucide-react';
import { teamAPI } from '../../services/api';
import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../contexts/ToastContext';

interface Hackathon {
  _id: string;
  title: string;
  eventType: string;
  maxTeamSize?: number;
  minTeamSize?: number;
  status: string;
  registrationDeadline: string;
  // Direct problem statements (from /hackathons endpoint)
  problemStatements?: Array<{
    _id: string;
    title: string;
    description: string;
    category: string;
    difficulty: string;
  }>;
  // Nested hackathon object (from /events endpoint)
  hackathon?: {
    maxTeamSize: number;
    minTeamSize: number;
    problemStatements: Array<{
      _id: string;
      title: string;
      description: string;
      category: string;
      difficulty: string;
    }>;
  };
}

const TeamCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hackathonId = searchParams.get('hackathon');
  const { success, error } = useToast();
  
  const [hackathon, setHackathon] = useState<Hackathon | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    selectedProblemStatement: null as { _id: string } | null
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Helper function to get problem statements regardless of data structure, filtering out hidden ones
  const getProblemStatements = () => {
    let statements: any[] = [];
    if (hackathon?.problemStatements) {
      statements = hackathon.problemStatements;
    } else if (hackathon?.hackathon?.problemStatements) {
      statements = hackathon.hackathon.problemStatements;
    }
    
    // Filter out problem statements that are hidden from teams
    return statements.filter((statement: any) => !statement.isHiddenFromTeams);
  };

  useEffect(() => {
    if (hackathonId) {
      fetchHackathon();
    } else {
      error('No hackathon selected');
      navigate('/hackathons');
    }
  }, [hackathonId]);

  const fetchHackathon = async () => {
    try {
      setLoading(true);
      
      // First try to fetch from hackathons endpoint
      let response;
      try {
        response = await api.get(`/hackathons/${hackathonId}`);
        const data = response.data;
        setHackathon(data);
      } catch (hackathonErr) {
        // If hackathon endpoint fails, try events endpoint
        console.log('Hackathon endpoint failed, trying events endpoint...');
        response = await api.get(`/events/${hackathonId}`);
        const data = response.data;
        
        // Check if it's actually a hackathon
        if (data.eventType !== 'hackathon') {
          error('This event is not a hackathon');
          navigate(`/events/${hackathonId}`);
          return;
        }
        
        setHackathon(data);
      }
      
      // No deadline check here. Everyone on this page has already registered;
      // the registration deadline is about joining the hackathon, not about
      // forming a team once you are in it. Turning people away past it stopped
      // team formation, which is the one thing that happens afterwards.
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch hackathon');
      navigate('/hackathons');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Team name must be at least 3 characters';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Team name must be less than 50 characters';
    }

    // Problem statement selection is optional
    // No validation needed as it's optional

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hackathonId) {
      error('Hackathon ID is required');
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSubmitting(true);
      const response = await teamAPI.createTeam({
        hackathonId: hackathonId,
        name: formData.name,
        selectedProblemStatement: formData.selectedProblemStatement || undefined
      });
      
      success('Team created successfully!');
      navigate(`/teams/${response.team._id}`);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to create team');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'selectedProblemStatement') {
      const problemStatement = getProblemStatements().find((ps: any) => ps._id === value);
      setFormData(prev => ({
        ...prev,
        selectedProblemStatement: problemStatement ? {
          _id: problemStatement._id,
          title: problemStatement.title,
          description: problemStatement.description,
          category: problemStatement.category,
          difficulty: problemStatement.difficulty
        } : null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!hackathon) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Team</h1>
        <p className="text-gray-600">Form a team for {hackathon.title}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Team Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Team Name *
              </label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter team name"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Problem Statement Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Problem Statement (Optional)
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="selectedProblemStatement" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Problem Statement
                  </label>
                  <select
                    id="selectedProblemStatement"
                    name="selectedProblemStatement"
                    value={formData.selectedProblemStatement?._id || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select a problem statement (optional) --</option>
                    {getProblemStatements().length > 0 ? (
                      getProblemStatements().map((problem) => (
                        <option key={problem._id} value={problem._id}>
                          {problem.title}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No problem statements available</option>
                    )}
                  </select>
                  
                  {/* Show selected problem statement details */}
                  {formData.selectedProblemStatement && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <h4 className="font-medium text-gray-900 mb-2">Selected Problem:</h4>
                      {(() => {
                        const selected = getProblemStatements().find(
                          (ps: any) => ps._id === formData.selectedProblemStatement?._id
                        );
                        return selected ? (
                          <div className="text-sm text-gray-700">
                            <p className="font-medium">{selected.title}</p>
                            <p className="mt-1">{selected.description}</p>
                            <p className="mt-1 text-gray-600">Category: {selected.category} • Difficulty: {selected.difficulty}</p>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
                
                {/* Tips about problem statement selection */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex items-center mb-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                    <h4 className="font-medium text-yellow-900">Important Notes</h4>
                  </div>
                  <div className="text-sm text-yellow-700 space-y-1">
                    <p>• Problem statement selection is optional during team formation</p>
                    <p>• If you don't select one, the admin will assign a problem statement for you</p>
                    <p>• Even if you select a problem statement, the admin may change it based on team composition</p>
                    <p>• You can view and update your assigned problem statement later in the team dashboard</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Information Alert */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-center mb-2">
                <AlertCircle className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="font-medium text-blue-900">Team Information</h4>
              </div>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• Team size: {hackathon.hackathon?.minTeamSize || hackathon.minTeamSize}-{hackathon.hackathon?.maxTeamSize || hackathon.maxTeamSize} members</p>
                <p>• You will be the team leader and can invite others using a join code</p>
                <p>• Team name can be updated later</p>
                <p>• Problem statement will be assigned by admin after team approval</p>
                <p>• <strong>Important:</strong> Team composition cannot be changed after the registration deadline ({new Date(hackathon.registrationDeadline).toLocaleDateString()} at {new Date(hackathon.registrationDeadline).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })})</p>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Creating Team...</span>
                  </>
                ) : (
                  'Create Team'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamCreate; 